import DOMPurify from "dompurify";
import { marked } from "marked";

// Configure marked options once at module initialization
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

/**
 * Converts Markdown to HTML
 * @param markdown - The markdown string to convert
 * @returns HTML string
 */
export function markdownToHtml(markdown: string): string {
  if (!markdown || typeof markdown !== 'string') return '';
  
  try {
    return marked.parse(markdown) as string;
  } catch (error) {
    console.error('Error converting markdown to HTML:', error);
    return markdown; // Return original if conversion fails
  }
}

/**
 * Converts plain text line breaks to HTML
 * Specifically handles \n\n as paragraph breaks and \n as line breaks
 * @param text - The plain text string to convert
 * @returns HTML string with proper line break handling
 */
export function convertLineBreaksToHtml(text: string): string {
  if (!text || typeof text !== 'string') return '';
  
  // Split by double newlines first to create paragraphs
  const paragraphs = text.split(/\n\n+/);
  
  // Convert each paragraph, handling single newlines as <br>
  const htmlParagraphs = paragraphs.map(para => {
    const trimmed = para.trim();
    if (!trimmed) return '';
    
    // Replace single newlines with <br>
    const withBreaks = trimmed.replace(/\n/g, '<br>');
    
    // Wrap in paragraph tags if it doesn't already have block-level HTML
    if (!/<(p|h[1-6]|div|ul|ol|blockquote|pre)/.test(withBreaks)) {
      return `<p>${withBreaks}</p>`;
    }
    
    return withBreaks;
  }).filter(p => p); // Remove empty paragraphs
  
  return htmlParagraphs.join('');
}

/**
 * Sanitizes HTML content using DOMPurify
 * Can optionally convert Markdown to HTML first
 * @param dirty - The HTML or Markdown string to sanitize
 * @param convertMarkdown - Whether to convert Markdown to HTML first (default: true)
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(dirty: string, convertMarkdown: boolean = true): string {

  if (!dirty || typeof dirty !== 'string') return '';

  let htmlContent = dirty;
  
  // Check if content looks like Markdown (contains ###, **, -, etc.)
  const hasMarkdownSyntax = /(\*\*|__|###|##|#|\n-|\n\*|\n\d+\.)/.test(dirty);
  
  // Check if content has line breaks that need to be converted
  const hasLineBreaks = /\n/.test(dirty);
  
  // Convert Markdown to HTML if needed and flag is true
  if (convertMarkdown && hasMarkdownSyntax) {
    htmlContent = markdownToHtml(dirty);
  } else if (convertMarkdown && hasLineBreaks && !/<[^>]+>/.test(dirty)) {
    // If no markdown syntax but has line breaks and no HTML tags, convert line breaks to HTML
    htmlContent = convertLineBreaksToHtml(dirty);
  }

  // This regex defines which URI schemes are allowed in links (e.g., in href/src attributes).
  // It is used to prevent XSS attacks by blocking dangerous protocols like javascript:,
  // data:, or vbscript: while allowing safe ones such as:
  // - http / https
  // - ftp / ftps
  // - mailto
  // - tel / callto
  // - cid
  // - xmpp
  //
  // It also allows relative URLs (e.g., /path, #anchor, ?query).
  // The regex is case-insensitive and ensures that any protocol not explicitly
  // whitelisted will be rejected for security reasons.
  return DOMPurify.sanitize(htmlContent, { 
    USE_PROFILES: { html: true },
    ADD_TAGS: ['b', 'strong', 'i', 'em', 'u', 'p', 'br', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'span', 'div', 'blockquote', 'pre', 'code'],
    ADD_ATTR: ['target', 'rel', 'href', 'class', 'id'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    ALLOW_DATA_ATTR: false
  });
}

export function sanitizeString(input: string): string {
  if (!input || typeof input !== 'string') return '';

  let clean = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true, // Keep text content but remove tags
  });

  clean = clean
    ?.replace(/&[#\w]+;/g, '') // Remove HTML entities like &lt; &gt; &amp;
    ?.replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
    ?.replace(/[<>]/g, '') // Remove angle brackets
    ?.replace(/javascript:/gi, '') // Remove javascript: protocol
    ?.replace(/on\w+\s*=/gi, '') // Remove event handlers
    ?.replace(/data:/gi, '') // Remove data: protocol
    ?.replace(/vbscript:/gi, '') // Remove vbscript: protocol
    ?.trim() ?? '';

  return clean;
}

export function isSafeString(input: string): boolean {

  if (!input || typeof input !== 'string') return true; 

  const clean = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
  const isValid = input === clean;
  return isValid;
}

export function validateXSSSafe(input: string): boolean {

  if (!input || typeof input !== 'string') return true; 

  const xssPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
    /<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi,
    /vbscript:/gi,
    /data:text\/html/gi
  ];
  
  return !xssPatterns.some(pattern => pattern.test(input));
}

export function sanitizeUrl(url: string): string | null {
  try {
    if (!url || typeof url !== 'string') return null;
    
    const urlObj = new URL(url);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return null;
    }
    return DOMPurify.sanitize(url);
  } catch {
    return null;
  }
}